import React, { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'

// Always show production URL in docs
const PRODUCTION_API_URL = 'https://app.squadline.com'

export default function DocsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
        <h2 className="text-lg font-semibold mb-4">API Overview</h2>
        <p className="text-zinc-300 mb-4">
          The Squadline Integration API allows external sports registration systems to sync 
          team, player, guardian, and event data with Squadline using OAuth2 authentication.
        </p>
        
        <div className="bg-zinc-900 rounded-lg p-4 mb-6">
          <p className="text-zinc-500 text-sm mb-2">Base URL (Production)</p>
          <code className="text-green-400 font-mono">{PRODUCTION_API_URL}/api/integrations</code>
        </div>

        <h3 className="font-semibold mt-6 mb-3">Authentication</h3>
        <p className="text-zinc-300 mb-4">
          Use OAuth2 Client Credentials flow to obtain an access token:
        </p>
        
        <div className="bg-zinc-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-zinc-500 font-mono whitespace-pre">{`POST /api/integrations/oauth/token
Content-Type: application/json

{
  "grant_type": "client_credentials",
  "client_id": "your_client_id",
  "client_secret": "your_client_secret"
}`}</pre>
        </div>

        <h3 className="font-semibold mt-6 mb-3">Per-Organization Authentication</h3>
        <p className="text-zinc-300 mb-4">
          Organizations authenticate with their own credentials for all API operations:
        </p>
        
        <div className="bg-zinc-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-zinc-500 font-mono whitespace-pre">{`POST /api/integrations/organizations/oauth/token
Content-Type: application/json

{
  "grant_type": "client_credentials",
  "client_id": "sq_org_xxx",
  "client_secret": "org_secret_xxx"
}`}</pre>
        </div>

        <h3 className="font-semibold mt-6 mb-3">Available Endpoints</h3>
        
        <p className="text-sm text-zinc-400 mb-4 font-medium">Partner Endpoints</p>
        <div className="space-y-4 mb-6">
          <EndpointDoc 
            method="POST" 
            path="/organizations/register" 
            description="Register a new organization"
            example={`{
  "external_org_id": "your_org_id",
  "name": "Organization Name",
  "billing_email": "billing@org.com",
  "contact_name": "Admin Name"
}`}
          />
        </div>

        <p className="text-sm text-zinc-400 mb-4 font-medium">Organization Endpoints</p>
        <div className="space-y-4">
          <EndpointDoc 
            method="POST" 
            path="/organizations/sync/roster" 
            description="Sync complete team roster"
            example={`{
  "external_team_id": "team_123",
  "team": {
    "external_id": "team_123",
    "name": "U14 Lightning",
    "sport": "Soccer"
  },
  "staff": [{
    "external_id": "coach_1",
    "name": "Coach Name",
    "email": "coach@email.com",
    "role": "coach"
  }],
  "players": [{
    "external_id": "player_1",
    "first_name": "John",
    "last_name": "Doe",
    "guardians": [{
      "external_id": "parent_1",
      "name": "Parent Name",
      "email": "parent@email.com"
    }]
  }],
  "send_invites": true
}`}
          />
          
          <EndpointDoc 
            method="POST" 
            path="/organizations/invites/generate" 
            description="Generate magic link invite"
            example={`{
  "team_id": "team_xxx",
  "email": "user@email.com",
  "role": "parent",
  "expires_days": 30
}`}
          />
          
          <EndpointDoc 
            method="GET" 
            path="/organizations/billing/seats" 
            description="Check seat balance"
          />
          
          <EndpointDoc 
            method="POST" 
            path="/organizations/billing/purchase-seats" 
            description="Purchase seats (Stripe checkout)"
            example={`Query params:
?quantity=10
&success_url=https://your-site.com/success
&cancel_url=https://your-site.com/cancel`}
          />
        </div>
        
        <p className="text-sm text-zinc-400 mb-4 mt-6 font-medium">Legacy Endpoints (Partner-level)</p>
        <div className="space-y-4">
          <EndpointDoc 
            method="POST" 
            path="/sync/teams" 
            description="Create or update a team"
            example={`{
  "external_id": "YOUR-TEAM-ID",
  "name": "Thunder U12",
  "sport": "Soccer",
  "season": "Spring 2026",
  "age_group": "U12"
}`}
          />
          
          <EndpointDoc 
            method="POST" 
            path="/sync/players" 
            description="Create or update a player"
            example={`{
  "external_id": "YOUR-PLAYER-ID",
  "external_team_id": "YOUR-TEAM-ID",
  "first_name": "John",
  "last_name": "Doe",
  "date_of_birth": "2014-05-15",
  "jersey_number": "10"
}`}
          />
          
          <EndpointDoc 
            method="POST" 
            path="/sync/guardians" 
            description="Create or update a guardian/parent"
            example={`{
  "external_id": "YOUR-GUARDIAN-ID",
  "external_player_ids": ["YOUR-PLAYER-ID"],
  "first_name": "Jane",
  "last_name": "Doe",
  "email": "jane@example.com",
  "phone": "+1234567890"
}`}
          />
        </div>
      </div>
    </div>
  )
}

function EndpointDoc({ method, path, description, example }) {
  const [expanded, setExpanded] = useState(false)
  
  const methodColors = {
    GET: 'bg-green-500/20 text-green-400',
    POST: 'bg-blue-500/20 text-blue-400',
    PUT: 'bg-yellow-500/20 text-yellow-400',
    DELETE: 'bg-red-500/20 text-red-400'
  }

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900 text-left"
      >
        <span className={`px-2 py-1 rounded text-xs font-bold ${methodColors[method]}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-zinc-200">{path}</code>
        <span className="text-sm text-zinc-400 ml-auto">{description}</span>
        <ArrowUpRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      
      {expanded && example && (
        <div className="border-t border-zinc-700 bg-zinc-900 p-4">
          <p className="text-zinc-500 text-xs mb-2">Request Body</p>
          <pre className="text-sm text-zinc-500 font-mono whitespace-pre overflow-x-auto">{example}</pre>
        </div>
      )}
    </div>
  )
}
